/**
 * OrgMovieMagicCrew - step-by-step production setup (onboarding).
 * Roster → Budget → Schedule → Day out of days → Invitations
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LpWorkbenchShell from '../components/org/LpWorkbenchShell';
import { orgAuthApi } from '../services/api';
import { parseMovieMagicCrewCsv } from '../utils/parseMovieMagicCrewCsv';
import { formatBudgetMillions, parseBudgetCsv } from '../utils/parseBudgetCsv';
import { parseScheduleCsv } from '../utils/parseScheduleCsv';
import { parseDayOutOfDaysCsv } from '../utils/parseDayOutOfDaysCsv';
import { formatApiDetail } from '../utils/apiError';
import { getLpName } from '../utils/orgLpHelpers';
import {
  invitationEmailTemplate,
  productionRegisterDisplayUrl,
} from '../utils/lpProductionHelpers';
import '../styles/LpWorkbench.css';
import '../styles/LpOnboard.css';

const STEP_LABELS = ['Roster', 'Budget', 'Schedule', 'Day out of days', 'Invitations'];
const FILE_STEP_COUNT = 4;
const INVITE_STEP = 4;

const STEPS = [
  {
    key: 'roster',
    head: 'Roster',
    sub: 'The cast and crew list for the production.',
    hint: 'or drop it here',
    acceptHint: '.xlsx or .csv from your production accountant',
    accept: '.csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    key: 'budget',
    head: 'Budget',
    sub: 'The budget exported from Movie Magic Budgeting.',
    hint: 'or drop it here',
    acceptHint: '.mmb or an exported .csv',
    accept: '.csv,.mmb,text/csv,application/octet-stream',
  },
  {
    key: 'schedule',
    head: 'Schedule',
    sub: 'The shooting schedule with locations and dates.',
    hint: 'or drop it here',
    acceptHint: '.xlsx or .csv',
    accept: '.csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    key: 'dayOutOfDays',
    head: 'Day out of days',
    sub: 'The day-out-of-days grid for cast and crew.',
    hint: 'or drop it here',
    acceptHint: '.xlsx or .csv',
    accept: '.csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
];

function isSpreadsheetFile(file) {
  if (!file) return false;
  return /\.(csv|xlsx|xls|mmb)$/i.test(file.name)
    || file.type === 'text/csv'
    || file.type === 'application/vnd.ms-excel'
    || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

function isCsvFile(file) {
  if (!file) return false;
  return /\.csv$/i.test(file.name)
    || file.type === 'text/csv'
    || file.type === 'application/vnd.ms-excel';
}

function buildRosterSummary(rows, warnings) {
  const roles = new Set(rows.map((r) => r.jobTitle || r.department).filter(Boolean));
  const countries = new Set(rows.map((r) => r.productionCountry).filter(Boolean));
  const nationalities = new Set(rows.map((r) => r.country).filter(Boolean));
  const missingNat = rows.filter((r) => !r.country).length;
  const nameCounts = {};
  rows.forEach((r) => {
    const n = (r.name || '').trim().toLowerCase();
    if (!n) return;
    nameCounts[n] = (nameCounts[n] || 0) + 1;
  });
  const dupNames = Object.values(nameCounts).filter((c) => c > 1).length;

  const flags = [];
  if (missingNat) flags.push({ ok: false, n: String(missingNat), text: `rows missing a nationality` });
  if (dupNames) flags.push({ ok: false, n: String(dupNames), text: `duplicate name${dupNames > 1 ? 's' : ''}` });
  if (warnings.length) flags.push({ ok: false, n: String(warnings.length), text: `row${warnings.length > 1 ? 's' : ''} skipped while reading` });
  if (!flags.length) flags.push({ ok: true, n: '✓', text: 'No issues found' });

  const previewCols = ['Name', 'Role', 'Phone', 'Nationality', 'Country'];
  const previewRows = rows.slice(0, 7).map((r) => [
    r.name || '-',
    r.jobTitle || r.department || '-',
    r.phone || '-',
    r.country || '-',
    r.productionCountry || '-',
  ]);

  return {
    summary: [
      [String(rows.length), 'People'],
      [String(roles.size), 'Roles'],
      [String(countries.size || 1), 'Country'],
      [String(nationalities.size), 'Nationalities'],
    ],
    flags,
    previewCols,
    previewRows,
  };
}

function formatLineAmountMillions(amount, currency) {
  return formatBudgetMillions(Number(amount), currency);
}

function buildStoredSummary(kind, file, meta = null) {
  if (kind === 'budget') {
    const budgetMeta = meta;
    const totalLabel = budgetMeta
      ? formatBudgetMillions(budgetMeta.total, budgetMeta.currency)
      : '-';
    const lineCount = budgetMeta?.lineCount != null ? String(budgetMeta.lineCount) : 'Stored';
    const currencyLabel = budgetMeta?.currency || '-';
    const previewCols = budgetMeta?.rows?.length
      ? ['Category', 'Description', 'Amount']
      : ['File', 'Status'];
    const previewRows = budgetMeta?.rows?.length
      ? budgetMeta.rows.slice(0, 7).map((r) => [
        r.category || '-',
        r.description || '-',
        formatLineAmountMillions(r.amount, r.currency || budgetMeta.currency),
      ])
      : [[file?.name || '-', 'Stored for later']];
    const flags = (budgetMeta?.warnings || []).length
      ? budgetMeta.warnings.slice(0, 3).map((text, i) => ({
        ok: false,
        n: String(i + 1),
        text,
      }))
      : [{ ok: true, n: '✓', text: 'No issues found' }];
    return {
      summary: [
        [totalLabel, 'Total'],
        [currencyLabel, 'Currency'],
        [lineCount, budgetMeta ? 'Lines' : 'File'],
      ],
      flags,
      previewCols,
      previewRows,
    };
  }
  if (kind === 'schedule') {
    const scheduleMeta = meta;
    const shootDays = scheduleMeta?.shootDays != null ? String(scheduleMeta.shootDays) : '-';
    const locations = scheduleMeta?.locationCount != null ? String(scheduleMeta.locationCount) : '-';
    const previewCols = scheduleMeta?.rows?.length
      ? ['Day', 'Date', 'Location']
      : ['File', 'Status'];
    const previewRows = scheduleMeta?.rows?.length
      ? scheduleMeta.rows.slice(0, 7).map((r) => [
        r.shootDay || '-',
        r.date || '-',
        r.location || '-',
      ])
      : [[file?.name || '-', 'Stored for later']];
    const flags = (scheduleMeta?.warnings || []).length
      ? scheduleMeta.warnings.slice(0, 3).map((text, i) => ({
        ok: false,
        n: String(i + 1),
        text,
      }))
      : [{ ok: true, n: '✓', text: 'No issues found' }];
    return {
      summary: [
        [shootDays, 'Shoot days'],
        [locations, 'Locations'],
        [file?.name?.split('.').pop()?.toUpperCase() || 'CSV', 'Format'],
      ],
      flags,
      previewCols,
      previewRows,
    };
  }
  if (kind === 'dayOutOfDays') {
    const doodMeta = meta;
    const people = doodMeta?.people != null ? String(doodMeta.people) : '-';
    const days = doodMeta?.days != null ? String(doodMeta.days) : '-';
    const previewCols = doodMeta?.rows?.length
      ? ['Name', 'Role', 'Work days']
      : ['File', 'Status'];
    const previewRows = doodMeta?.rows?.length
      ? doodMeta.rows.slice(0, 7).map((r) => [
        r.name || '-',
        r.role || '-',
        String(r.workDays ?? '-'),
      ])
      : [[file?.name || '-', 'Stored for later']];
    const flags = (doodMeta?.warnings || []).length
      ? doodMeta.warnings.slice(0, 3).map((text, i) => ({
        ok: false,
        n: String(i + 1),
        text,
      }))
      : [{ ok: true, n: '✓', text: 'No issues found' }];
    return {
      summary: [
        [people, 'People'],
        [days, 'Days'],
        [file?.name?.split('.').pop()?.toUpperCase() || 'CSV', 'Format'],
      ],
      flags,
      previewCols,
      previewRows,
    };
  }
  return {
    summary: [
      ['-', 'Shoot days'],
      ['-', 'Locations'],
      ['Stored', 'File'],
    ],
    flags: [{ ok: true, n: '✓', text: 'No issues found' }],
    previewCols: ['File', 'Status'],
    previewRows: [[file?.name || '-', 'Stored for later']],
  };
}

function OnboardStepper({ step, uploaded }) {
  const lastIndex = STEP_LABELS.length - 1;
  return (
    <div className="ob-steps" aria-label="Setup progress">
      {STEP_LABELS.map((label, i) => {
        const done = (i < FILE_STEP_COUNT && uploaded[i]) || i < step;
        const on = step === i;
        const cls = `ob-step${on ? ' on' : ''}${done && !on ? ' done' : ''}`;
        return (
          <React.Fragment key={label}>
            <div className={cls}>
              <span className="ob-num">{done && !on ? '✓' : i + 1}</span>
              <span className="ob-slabel">{label}</span>
            </div>
            {i < lastIndex ? (
              <div className={`ob-line${(i < step || (uploaded[i] && step > i)) ? ' done' : ''}`} />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrgMovieMagicCrew() {
  const navigate = useNavigate();
  const location = useLocation();
  const projectNameHint = location.state?.projectName || '';
  const projectIdFromState = location.state?.projectId;
  const isExistingProject = Boolean(projectIdFromState);

  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [uploaded, setUploaded] = useState([false, false, false, false]);
  const [rosterFile, setRosterFile] = useState(null);
  const [budgetFile, setBudgetFile] = useState(null);
  const [budgetMeta, setBudgetMeta] = useState(null);
  const [scheduleFile, setScheduleFile] = useState(null);
  const [scheduleMeta, setScheduleMeta] = useState(null);
  const [dayOutOfDaysFile, setDayOutOfDaysFile] = useState(null);
  const [dayOutOfDaysMeta, setDayOutOfDaysMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState(projectIdFromState || null);
  const [signupRef, setSignupRef] = useState('');
  const [signupUrl, setSignupUrl] = useState('');
  const [crewImported, setCrewImported] = useState(false);

  const productionName = useMemo(() => {
    return rows[0]?.projectName || projectNameHint || 'your production';
  }, [rows, projectNameHint]);

  const lpName = getLpName() || 'Production';

  const registerUrl = useMemo(
    () => productionRegisterDisplayUrl(signupRef, signupUrl),
    [signupRef, signupUrl],
  );

  const inviteTpl = useMemo(
    () => invitationEmailTemplate({
      projectName: productionName,
      signupUrl: registerUrl || undefined,
      producerName: lpName,
      productionCode: signupRef,
    }),
    [productionName, registerUrl, lpName, signupRef],
  );

  useEffect(() => {
    if (!projectIdFromState) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await orgAuthApi.getProduction(projectIdFromState);
        if (cancelled) return;
        const project = res.data?.project;
        const ref = project?.customer_signup_ref || '';
        setSignupRef(ref);
        setSignupUrl(project?.customer_signup_url || '');
        setResolvedProjectId(project?.id || projectIdFromState);
      } catch {
        /* invite step will surface error if still missing */
      }
    })();
    return () => { cancelled = true; };
  }, [projectIdFromState]);

  async function copyInvite() {
    const text = `Subject: ${inviteTpl.subject}\n\n${inviteTpl.bodyText}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  async function ensureCrewImported() {
    if (crewImported && resolvedProjectId) {
      return resolvedProjectId;
    }
    if (!rosterFile) {
      throw new Error('Roster is required before finishing setup.');
    }
    const extras = {
      scheduleFile: scheduleFile || undefined,
      dayOutOfDaysFile: dayOutOfDaysFile || undefined,
      budgetFile: budgetFile || undefined,
    };
    const res = resolvedProjectId || projectIdFromState
      ? await orgAuthApi.importCrewAndInvite(resolvedProjectId || projectIdFromState, rosterFile, extras)
      : await orgAuthApi.importCrewAndInviteNewProject(rosterFile, extras);
    const data = res.data || {};
    const pid = data.project_id || resolvedProjectId || projectIdFromState;
    const ref = data.customer_signup_ref || '';
    if (ref) setSignupRef(ref);
    if (data.customer_signup_url) setSignupUrl(data.customer_signup_url);
    if (pid) setResolvedProjectId(pid);
    setCrewImported(true);
    return pid;
  }

  const confirmData = useMemo(() => {
    if (step === 0 && rosterFile && rows.length) return buildRosterSummary(rows, warnings);
    if (step === 1 && budgetFile) return buildStoredSummary('budget', budgetFile, budgetMeta);
    if (step === 2 && scheduleFile) return buildStoredSummary('schedule', scheduleFile, scheduleMeta);
    if (step === 3 && dayOutOfDaysFile) {
      return buildStoredSummary('dayOutOfDays', dayOutOfDaysFile, dayOutOfDaysMeta);
    }
    return null;
  }, [
    step,
    rosterFile,
    budgetFile,
    budgetMeta,
    scheduleFile,
    scheduleMeta,
    dayOutOfDaysFile,
    dayOutOfDaysMeta,
    rows,
    warnings,
  ]);

  const handleRosterFile = useCallback((file) => {
    if (!file) return;
    if (!isCsvFile(file)) {
      setParseError('Roster must be a .csv export (Movie Magic / accountant roster).');
      setRosterFile(null);
      setRows([]);
      setWarnings([]);
      setUploaded((u) => { const n = [...u]; n[0] = false; return n; });
      return;
    }
    setParsing(true);
    setParseError('');
    setSubmitError('');
    setRosterFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = parseMovieMagicCrewCsv(reader.result);
        if (!result.rows?.length) {
          throw new Error('No people found in this roster file.');
        }
        setRows(result.rows);
        setWarnings(result.warnings || []);
        setUploaded((u) => { const n = [...u]; n[0] = true; return n; });
        setPreviewOpen(false);
      } catch (err) {
        setRows([]);
        setWarnings([]);
        setRosterFile(null);
        setUploaded((u) => { const n = [...u]; n[0] = false; return n; });
        setParseError(err?.message || 'Could not read this roster CSV.');
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setParsing(false);
      setRosterFile(null);
      setParseError('Could not read the roster file.');
    };
    reader.readAsText(file);
  }, []);

  const handleBudgetFile = useCallback((file) => {
    if (!file) return;
    if (!isSpreadsheetFile(file)) {
      setParseError('Please upload a .csv or Movie Magic budget export.');
      return;
    }
    setParseError('');
    setSubmitError('');
    setBudgetFile(file);
    setBudgetMeta(null);
    setUploaded((u) => { const n = [...u]; n[1] = true; return n; });
    setPreviewOpen(false);

    // Parse totals from CSV exports; binary .mmb stays "stored only".
    if (!isCsvFile(file)) return;

    setParsing(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseBudgetCsv(reader.result);
        setBudgetMeta({
          total: parsed.total,
          currency: parsed.currency,
          lineCount: parsed.lineCount,
          rows: parsed.rows,
          warnings: parsed.warnings || [],
        });
      } catch (err) {
        setBudgetMeta(null);
        setParseError(err?.message || 'Could not read budget totals from this CSV.');
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setParsing(false);
      setBudgetMeta(null);
      setParseError('Could not read the budget file.');
    };
    reader.readAsText(file);
  }, []);

  const handleScheduleFile = useCallback((file) => {
    if (!file) return;
    if (!isSpreadsheetFile(file)) {
      setParseError('Please upload a .csv, .xlsx, or compatible export.');
      return;
    }
    setParseError('');
    setSubmitError('');
    setScheduleFile(file);
    setScheduleMeta(null);
    setUploaded((u) => { const n = [...u]; n[2] = true; return n; });
    setPreviewOpen(false);

    if (!isCsvFile(file)) return;

    setParsing(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseScheduleCsv(reader.result);
        setScheduleMeta({
          shootDays: parsed.shootDays,
          locationCount: parsed.locationCount,
          rowCount: parsed.rowCount,
          rows: parsed.rows,
          warnings: parsed.warnings || [],
        });
      } catch (err) {
        setScheduleMeta(null);
        setParseError(err?.message || 'Could not read this schedule CSV.');
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setParsing(false);
      setScheduleMeta(null);
      setParseError('Could not read the schedule file.');
    };
    reader.readAsText(file);
  }, []);

  const handleDayOutOfDaysFile = useCallback((file) => {
    if (!file) return;
    if (!isSpreadsheetFile(file)) {
      setParseError('Please upload a .csv, .xlsx, or compatible export.');
      return;
    }
    setParseError('');
    setSubmitError('');
    setDayOutOfDaysFile(file);
    setDayOutOfDaysMeta(null);
    setUploaded((u) => { const n = [...u]; n[3] = true; return n; });
    setPreviewOpen(false);

    if (!isCsvFile(file)) return;

    setParsing(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseDayOutOfDaysCsv(reader.result);
        setDayOutOfDaysMeta({
          people: parsed.people,
          days: parsed.days,
          rowCount: parsed.rowCount,
          rows: parsed.rows,
          warnings: parsed.warnings || [],
        });
      } catch (err) {
        setDayOutOfDaysMeta(null);
        setParseError(err?.message || 'Could not read this day-out-of-days CSV.');
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setParsing(false);
      setDayOutOfDaysMeta(null);
      setParseError('Could not read the day-out-of-days file.');
    };
    reader.readAsText(file);
  }, []);

  function acceptFile(file) {
    if (step === 0) handleRosterFile(file);
    else if (step === 1) handleBudgetFile(file);
    else if (step === 2) handleScheduleFile(file);
    else if (step === 3) handleDayOutOfDaysFile(file);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0] || null);
  }

  async function finishSetup({ sent }) {
    if (!rosterFile || !rows.length) {
      setSubmitError('Roster is required before finishing setup.');
      setStep(0);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const pid = await ensureCrewImported();
      if (pid) {
        navigate(`/org/productions/${pid}`, {
          replace: true,
          state: { invitationSent: Boolean(sent) },
        });
        return;
      }
      navigate('/org/slate', { replace: true });
    } catch (err) {
      setSubmitError(formatApiDetail(err?.response?.data?.detail) || err?.message || 'Could not create the production. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function goNext() {
    if (step < FILE_STEP_COUNT && !uploaded[step]) return;

    // Entering invitations: create/load production so the magic link is real.
    if (step === FILE_STEP_COUNT - 1) {
      if (!rosterFile || !rows.length) {
        setSubmitError('Roster is required before invitations.');
        setStep(0);
        return;
      }
      setSubmitting(true);
      setSubmitError('');
      try {
        if (isExistingProject && signupRef) {
          setStep(INVITE_STEP);
          setPreviewOpen(false);
          return;
        }
        if (isExistingProject && !signupRef && projectIdFromState) {
          const res = await orgAuthApi.getProduction(projectIdFromState);
          const project = res.data?.project;
          const ref = project?.customer_signup_ref || '';
          setSignupRef(ref);
          setSignupUrl(project?.customer_signup_url || '');
          setResolvedProjectId(project?.id || projectIdFromState);
          if (!ref) {
            // Ensure ref exists by importing (creates signup ref via ensure_project_customer_signup_ref)
            await ensureCrewImported();
          }
          setStep(INVITE_STEP);
          setPreviewOpen(false);
          return;
        }
        await ensureCrewImported();
        setStep(INVITE_STEP);
        setPreviewOpen(false);
      } catch (err) {
        setSubmitError(formatApiDetail(err?.response?.data?.detail) || err?.message || 'Could not prepare the registration link.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (step < FILE_STEP_COUNT) {
      setStep((s) => s + 1);
      setPreviewOpen(false);
      setParseError('');
      return;
    }
    finishSetup({ sent: true });
  }

  function goBack() {
    if (step === 0) {
      navigate('/org/slate');
      return;
    }
    setStep((s) => s - 1);
    setPreviewOpen(false);
    setParseError('');
    setSubmitError('');
  }

  const files = [rosterFile, budgetFile, scheduleFile, dayOutOfDaysFile];

  const st = STEPS[step] || null;
  const stepDone = step < FILE_STEP_COUNT && uploaded[step];

  return (
    <LpWorkbenchShell>
      <div className="ob-stage">
        <div className="ob-col">
          <div className="ob-eyebrow">Set up {productionName === 'your production' ? 'production' : productionName}</div>
          <OnboardStepper step={step} uploaded={uploaded} />

          {step < FILE_STEP_COUNT && st ? (
            <div className="ob-panel">
              <div className="ob-stephead">{st.head}</div>
              {!stepDone ? (
                <>
                  <div className="ob-stepsub">{st.sub}</div>
                  <div
                    className={`ob-drop${dragOver ? ' drag' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => { if (!parsing) fileInputRef.current?.click(); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    <div className="ob-dropicon">↑</div>
                    <div className="ob-dropmain">Choose a file {st.hint}</div>
                    <div className="ob-hint">{parsing ? 'Reading file…' : st.acceptHint}</div>
                    <button
                      type="button"
                      className="ob-uploadbtn"
                      id="obUp"
                      disabled={parsing}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Choose file
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={st.accept}
                      className="lp-file-input"
                      onChange={(e) => {
                        acceptFile(e.target.files?.[0] || null);
                        e.target.value = '';
                      }}
                      aria-label={`Upload ${st.head}`}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="ob-fileline">
                    <span className="ob-fdot" />
                    {files[step]?.name}
                  </div>
                  {confirmData ? (
                    <>
                      <div className="ob-sumgrid">
                        {confirmData.summary.map(([num, lbl]) => (
                          <div className="ob-sumcell" key={lbl}>
                            <div className="ob-sumnum">{num}</div>
                            <div className="ob-sumlbl">{lbl}</div>
                          </div>
                        ))}
                      </div>
                      <div className="ob-flags">
                        {confirmData.flags.map((fl) => (
                          <div className={`ob-flag${fl.ok ? ' ok' : ''}`} key={fl.text}>
                            <span className="ob-fn">{fl.n}</span>
                            <span>{fl.text}</span>
                          </div>
                        ))}
                      </div>
                      <div className="ob-preview">
                        <button
                          type="button"
                          className="ob-previewtoggle"
                          onClick={() => setPreviewOpen((v) => !v)}
                        >
                          {previewOpen ? 'Hide rows ▴' : 'Preview rows ▾'}
                        </button>
                        <div className={`ob-previewbox${previewOpen ? ' open' : ''}`}>
                          <table className="ob-ptbl">
                            <thead>
                              <tr>
                                {confirmData.previewCols.map((c) => <th key={c}>{c}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {confirmData.previewRows.map((r, i) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={i}>
                                  {r.map((c, j) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <td key={j}>{c}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="ob-confirmnote">
                        Confirm this reads correctly. You can re-import the file later if it changes.
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {step === INVITE_STEP ? (
            <div className="ob-panel">
              <div className="ob-stephead">Send the invitation</div>
              <div className="ob-stepsub">
                Global Mobility emails each person their personal registration link from noreply@globalmobility.law.
                This is the invitation they will receive.
              </div>
              <div className="ob-invp">
                Each link can only be used once and will expire. Participants register with the email address the production has on file.
              </div>
              {submitting && !registerUrl ? (
                <div className="ob-confirmnote">Preparing registration link…</div>
              ) : null}
              <div className="ob-mailcard lp-invite-mail-card">
                <div className="lp-invite-mail-subject">
                  <span>Subject</span>
                  <strong>{inviteTpl.subject}</strong>
                </div>
                <div
                  className="lp-invite-mail-preview"
                  dangerouslySetInnerHTML={{ __html: inviteTpl.bodyHtml }}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="lp-bn-btn ghost"
                  onClick={copyInvite}
                  disabled={!registerUrl || submitting}
                >
                  {copied ? 'Copied' : 'Copy email'}
                </button>
              </div>
            </div>
          ) : null}

          {parseError ? (
            <div className="lp-banner" style={{ background: '#FBF1EF', borderColor: '#E8C4BE', marginTop: 16 }}>
              <div className="lp-bn-txt"><b>{parseError}</b></div>
            </div>
          ) : null}
          {submitError ? (
            <div className="lp-banner" style={{ background: '#FBF1EF', borderColor: '#E8C4BE', marginTop: 16 }}>
              <div className="lp-bn-txt"><b>{submitError}</b></div>
            </div>
          ) : null}

          <div className="ob-actions">
            {step > 0 ? (
              <button
                type="button"
                className="lp-bn-btn ghost"
                onClick={goBack}
                disabled={submitting}
              >
                Back
              </button>
            ) : null}
            {step === INVITE_STEP ? (
              <button
                type="button"
                className="ob-skip"
                onClick={() => finishSetup({ sent: false })}
                disabled={submitting}
              >
                Skip for now
              </button>
            ) : null}
            <div className="ob-spacer" />
            {step < FILE_STEP_COUNT ? (
              <button
                type="button"
                className="lp-bn-btn"
                onClick={goNext}
                disabled={!stepDone || parsing || submitting}
                style={!stepDone || parsing ? { opacity: 0.5 } : undefined}
              >
                Confirm and continue
              </button>
            ) : (
              <button
                type="button"
                className="lp-bn-btn"
                onClick={() => finishSetup({ sent: true })}
                disabled={submitting}
              >
                {submitting ? 'Saving…' : "I've sent it"}
              </button>
            )}
          </div>
        </div>
      </div>
    </LpWorkbenchShell>
  );
}
